import { memo, useCallback, useMemo } from 'react';

import { Month } from '../../types';
import { getCurrentMonth, getFrequencyMonths, getMonthsBetweenRange, incrementMonth, monthsListIncludes } from '../../utils';
import KpiDynamicCell from '../KpiDynamicCell/KpiDynamicCell';

interface Props {
    id: number;
    month: Month;
    kpi: any;
    isCustomer: boolean;
    getDataForMonth: (index: number, month: number, year: number) => number | '';
    setDataForMonth: (index: number, month: number, year: number, amount: number | '') => void;
    shouldFocusMe: boolean;
    setShouldFocusCellId: (value: number | null) => void;
    setDirtyCell: (v: any) => void;
    editDisabled: boolean;
}

const KpiValueCell = (props: Props) => {
    const { month, kpi, getDataForMonth, setDataForMonth, setDirtyCell, ...otherProps } = props;

    const setExternalValue = useCallback((value: string) => {
        setDataForMonth(kpi.id, month.month, month.year, value === '' ? value : parseFloat(value));
    }, [kpi.id, month, setDataForMonth]);

    const getExternalValue = useCallback(() => {
        return getDataForMonth(kpi.id, month.month, month.year).toString().replaceAll(',', '.');
    }, [kpi.id, month, getDataForMonth]);

    const setExternalDirtyCell = useCallback(() => {
        setDirtyCell({ id: kpi.id, m: month.month, y: month.year });
    }, [kpi.id, month, setDirtyCell]);

    const formatInternalValue = useCallback((value: string) => {
        return value.replaceAll('.', ',');
    }, []);

    const backgroundColor = useMemo(() => {
        const lastMonth = incrementMonth(month, getFrequencyMonths(kpi.monitoringFrequency) - 1);
        const monthsList = getMonthsBetweenRange(month, lastMonth);
        const dueDateSplitted = (kpi.dueDate ?? '00/00/0000').split('/').map((n: any) => parseInt(n, 10));

        if (dueDateSplitted[1] === month.month && dueDateSplitted[2] === month.year) {
            // orange background if the cell is due date month
            return '#fff4e5';
        } else if (monthsListIncludes(monthsList, getCurrentMonth())) {
            // green background if the cell is the current month
            return '#edf7ed';
        } else {
            return undefined;
        }
    }, [kpi, month]);

    const width = useMemo(() => {
        return getFrequencyMonths(kpi.monitoringFrequency) * 100 + 'px';
    }, [kpi]);

    return (
        <KpiDynamicCell
            getExternalValue={getExternalValue}
            setExternalValue={setExternalValue}
            setExternalDirtyCell={setExternalDirtyCell}
            formatInternalValue={formatInternalValue}
            tableCellProps={{ style: { backgroundColor }, colSpan: getFrequencyMonths(kpi.monitoringFrequency) }}
            textFieldProps={{ style: { width, backgroundColor }, type: 'number' }}
            inputProps={{ min: 0 }}
            divStyle={{ width, padding: '0 14px', marginTop: '1px' }}
            {...otherProps}
        />
    );
};

export default memo(KpiValueCell);
